import * as auth0 from 'auth0-js';

import { User } from './types/User';
import config from './config';

function resolveBaseUrl(): string {
  try {
    const hasWindow = typeof window !== 'undefined';
    const origin = hasWindow ? window.location.origin : '';
    const cfg = (config as any)?.REACT_APP_BASEURL as string | undefined;
    const cfgTrim = (cfg || '').replace(/\/$/, '');
    if (cfgTrim && /localhost/i.test(cfgTrim) && origin && !/localhost/i.test(origin)) {
      return origin;
    }
    return cfgTrim || origin;
  } catch {
    return (config as any)?.REACT_APP_BASEURL || '';
  }
}

class Auth {
  webAuth: auth0.WebAuth;
  profile: User;
  idToken: string;
  accessToken: string;
  expiresAt: number;

  constructor() {
    const baseUrl = resolveBaseUrl();
    this.webAuth = new auth0.WebAuth({
      clientID: 'cAMKBUEgzKhj6eboaGv9qEh6zw314He5',
      domain: 'ferritico.eu.auth0.com',
      redirectUri: `${baseUrl}/auth-callback`,
      responseType: 'token id_token',
      scope: 'openid profile email email_verified'
    });

    this.getProfile = this.getProfile.bind(this);
    this.handleAuthentication = this.handleAuthentication.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
    this.signIn = this.signIn.bind(this);
    this.signOut = this.signOut.bind(this);

    // Attempt to restore session from localStorage on init
    try {
      const storedId = localStorage.getItem('auth.idToken');
      const storedAccess = localStorage.getItem('auth.accessToken');
      const storedExp = localStorage.getItem('auth.expiresAt');
      const storedProfile = localStorage.getItem('auth.profile');
      if (storedId && storedAccess && storedExp) {
        this.idToken = storedId;
        this.accessToken = storedAccess;
        this.expiresAt = parseInt(storedExp, 10);
      }
      if (storedProfile) {
        this.profile = JSON.parse(storedProfile);
      }
      // If a stale session is found, clear it immediately
      if (this.expiresAt && new Date().getTime() >= this.expiresAt) {
        this.clearLocalSession();
      }
    } catch (e) {
      // ignore storage errors
    }
  }

  getProfile() {
    return this.profile;
  }

  getIdToken() {
    return this.idToken;
  }

  getAccessToken() {
    return this.accessToken;
  }

  getExpiresAt() {
    return this.expiresAt;
  }

  setSession(authResult: any) {
    this.idToken = authResult.idToken;
    this.accessToken = authResult.accessToken;

    const ns = `${config.AUTH0_NAMESPACE}`;
    const rawRoles = authResult?.idTokenPayload?.[ns + "/roles"];
    const rolesFromId = Array.isArray(rawRoles) ? rawRoles : [];

    // Normalize to canonical roles used across app: 'Free-User', 'User', 'Admin', 'Product2'.
    const roles = rolesFromId.reduce((acc: any[], r: any) => {
      const v = (typeof r === 'string' ? r : '').toLowerCase();
      let mapped: string | null = null;
      if (v === 'user') mapped = 'User';
      else if (v === 'free-user' || v === 'free_user' || v === 'free' || v === 'basic') mapped = 'Free-User';
      else if (v === 'admin') mapped = 'Admin';
      else if (v === 'product2' || v === 'product-2' || v === 'product_2') mapped = 'Product2';
      if (mapped && !acc.includes(mapped)) acc.push(mapped);
      return acc;
    }, [] as string[]);

    this.profile = {
      email: authResult.idTokenPayload.email,
      emailVerified: authResult.idTokenPayload.email_verified,
      name: authResult.idTokenPayload.name,
      picture: authResult.idTokenPayload.picture,
      trialRemaining: authResult.idTokenPayload?.[ns + "/trial_days_remaining"],
      trialExpired: authResult.idTokenPayload?.[ns + "/trial_expired"],
      roles: roles
    };

    // set the time that the id token will expire at
    this.expiresAt = authResult.expiresIn * 1000 + new Date().getTime();

    // persist to localStorage
    try {
      localStorage.setItem('auth.idToken', this.idToken);
      localStorage.setItem('auth.accessToken', this.accessToken);
      localStorage.setItem('auth.expiresAt', String(this.expiresAt));
      localStorage.setItem('auth.profile', JSON.stringify(this.profile));
    } catch (e) {
      // ignore storage errors
    }
  }

  handleAuthentication() {
    return new Promise<void>((resolve, reject) => {
      this.webAuth.parseHash((err, authResult) => {
        if (err) {
          return reject(err);
        }
        if (!authResult || !authResult.idToken) {
          return reject(err);
        }
        this.setSession(authResult);
        resolve();
      });
    });
  }

  isAuthenticated() {
    const now = new Date().getTime();
    const valid = !!this.expiresAt && now < this.expiresAt;
    return valid;
  }

  signIn() {
    this.webAuth.authorize();
  }

  signOut() {
    const baseUrl = resolveBaseUrl();
    this.webAuth.logout({
      clientID: 'cAMKBUEgzKhj6eboaGv9qEh6zw314He5',
      returnTo: `${baseUrl}`
    });
    this.clearLocalSession();
  }

  silentAuth() {
    return new Promise<void>((resolve, reject) => {
      this.webAuth.checkSession({}, (err, authResult) => {
        if (err) {
          return reject(err);
        }
        this.setSession(authResult);
        resolve();
      });
    });
  }

  // Remove locally stored session without hitting Auth0
  clearLocalSession() {
    try {
      localStorage.removeItem('auth.idToken');
      localStorage.removeItem('auth.accessToken');
      localStorage.removeItem('auth.expiresAt');
      localStorage.removeItem('auth.profile');
    } catch { }
    this.idToken = undefined as any;
    this.accessToken = undefined as any;
    this.expiresAt = 0 as any;
    this.profile = undefined as any;
  }
}

const auth0Client = new Auth();

export default auth0Client;
